import { Country, State, City } from 'country-state-city';

export const NO_STATE_PLACEHOLDER = '---';

/**
 * Returns all countries as select options.
 * @returns {{ label: string, isoCode: string }[]}
 */
export function getCountryOptions() {
  return Country.getAllCountries().map((c) => ({
    label: c.name,
    isoCode: c.isoCode,
  }));
}

/**
 * Returns state/province options for a given country ISO code.
 * If the country has no states, returns a single placeholder option.
 * @param {string} countryIso
 * @returns {{ label: string, isoCode: string | null }[]}
 */
export function getStateOptions(countryIso) {
  if (!countryIso) return [];
  const states = State.getStatesOfCountry(countryIso);
  if (states.length === 0) {
    return [{ label: NO_STATE_PLACEHOLDER, isoCode: null }];
  }
  return states.map((s) => ({ label: s.name, isoCode: s.isoCode }));
}

/**
 * Returns city options for a given country ISO code and state ISO code.
 * If the state is the placeholder (country has no states), returns all
 * cities for the country instead.
 * @param {string} countryIso
 * @param {string | null} stateIso  — null when state is the placeholder
 * @returns {{ label: string }[]}
 */
export function getCityOptions(countryIso, stateIso) {
  if (!countryIso) return [];
  const cities =
    stateIso === null
      ? City.getCitiesOfCountry(countryIso)
      : City.getCitiesOfState(countryIso, stateIso);
  return (cities ?? []).map((c) => ({ label: c.name }));
}

/**
 * Validates that (countryName, stateName, cityName) form a valid path in
 * the Country → State/Province → City tree.
 *
 * Rules:
 *  - countryName must be a known country.
 *  - If the country has states, stateName must be a valid state of that country
 *    and cityName must be a valid city of that state.
 *  - If the country has NO states, stateName must equal NO_STATE_PLACEHOLDER ('---')
 *    and cityName must be a valid city within the country.
 *
 * @param {string} countryName
 * @param {string} stateName
 * @param {string} cityName
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateLocationTree(countryName, stateName, cityName) {
  const country = Country.getAllCountries().find((c) => c.name === countryName);
  if (!country) return { valid: false, reason: 'Invalid country.' };

  const states = State.getStatesOfCountry(country.isoCode);
  const hasStates = states.length > 0;

  if (!hasStates) {
    if (stateName !== NO_STATE_PLACEHOLDER) {
      return {
        valid: false,
        reason: `${countryName} has no states or provinces. State/Province must be "${NO_STATE_PLACEHOLDER}".`,
      };
    }
    const cities = City.getCitiesOfCountry(country.isoCode) ?? [];
    const cityMatch = cities.find((c) => c.name === cityName);
    return cityMatch
      ? { valid: true }
      : { valid: false, reason: `"${cityName}" is not a valid city in ${countryName}.` };
  }

  const state = states.find((s) => s.name === stateName);
  if (!state) {
    return {
      valid: false,
      reason: `"${stateName}" is not a valid state or province of ${countryName}.`,
    };
  }

  const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
  const cityMatch = cities.find((c) => c.name === cityName);
  return cityMatch
    ? { valid: true }
    : {
        valid: false,
        reason: `"${cityName}" is not a valid city in ${stateName}, ${countryName}.`,
      };
}

/**
 * Resolve a stored country value to its ISO code.
 *
 * Historic data is inconsistent — onboarding sends the ISO code ("CA") while some rows hold the
 * display name ("Canada") — so accept either and let the cascading dropdowns work regardless.
 * Returns '' when it can't be resolved.
 */
export function resolveCountryIso(value) {
  if (!value) return '';
  const v = String(value).trim();
  const opts = getCountryOptions();
  const byIso = opts.find((c) => c.isoCode.toLowerCase() === v.toLowerCase());
  if (byIso) return byIso.isoCode;
  const byName = opts.find((c) => c.label.toLowerCase() === v.toLowerCase());
  return byName ? byName.isoCode : '';
}
